import React, { useEffect, useRef } from "react";

const applyLightTheme = (element) => {
	if (!element) return;

	// Force Google's default Places dropdown into light/white theme
	// (otherwise it follows OS dark mode and renders black).
	element.style.colorScheme = "light";
	element.style.setProperty("color-scheme", "light");
	element.style.width = "100%";
	element.style.display = "block";
	element.style.boxSizing = "border-box";
	element.style.backgroundColor = "#ffffff";
	element.style.border = "1px solid #d0d0d0";
	element.style.borderRadius = "18px";
	element.style.fontFamily = "inherit";
	element.style.fontSize = "14px";
	element.style.color = "#30333a";
	element.style.setProperty("--gmp-mat-color-surface", "#ffffff");
	element.style.setProperty("--gmp-mat-color-on-surface", "#30333a");
	element.style.setProperty("--gmp-mat-color-on-surface-variant", "#8f8f8f");
	element.style.setProperty("--gmp-mat-color-primary", "#536d80");
};

const getPlaceLabel = (place, fallback = "") => {
	const displayName =
		typeof place?.displayName === "string"
			? place.displayName
			: place?.displayName?.text || "";
	return place?.formattedAddress || displayName || fallback;
};

const KycMapPlaceInput = ({
	value = "",
	onChange,
	onPlaceSelected,
	placeholder = "Search location",
}) => {
	const containerRef = useRef(null);
	const elementRef = useRef(null);
	const onChangeRef = useRef(onChange);
	const onPlaceSelectedRef = useRef(onPlaceSelected);

	onChangeRef.current = onChange;
	onPlaceSelectedRef.current = onPlaceSelected;

	useEffect(() => {
		let isMounted = true;
		let placeAutocomplete = null;
		let inputListener = null;

		const handleSelect = async (event) => {
			try {
				const placePrediction = event?.placePrediction;
				if (!placePrediction?.toPlace) return;

				const place = placePrediction.toPlace();
				await place.fetchFields({
					fields: ["displayName", "formattedAddress", "location", "addressComponents", "id"],
				});

				if (!isMounted || !place.location) return;

				const label = getPlaceLabel(place, placeAutocomplete?.value || "");
				if (placeAutocomplete && label) {
					placeAutocomplete.value = label;
				}

				onChangeRef.current?.(label);
				onPlaceSelectedRef.current?.(place);
			} catch (error) {
				console.error("Failed to resolve selected place", error);
			}
		};

		const init = async () => {
			if (!containerRef.current || !window.google?.maps?.importLibrary) return;

			const placesLibrary = await window.google.maps.importLibrary("places");
			const PlaceAutocompleteElement =
				placesLibrary.PlaceAutocompleteElement ||
				window.google.maps.places?.PlaceAutocompleteElement;

			if (!PlaceAutocompleteElement || !isMounted || !containerRef.current) {
				return;
			}

			placeAutocomplete = new PlaceAutocompleteElement({
				requestedLanguage: "en",
				requestedRegion: "in",
			});
			placeAutocomplete.className = "kyc-map-place-autocomplete";
			placeAutocomplete.placeholder = placeholder;
			applyLightTheme(placeAutocomplete);

			if (value) {
				placeAutocomplete.value = value;
			}

			containerRef.current.innerHTML = "";
			containerRef.current.appendChild(placeAutocomplete);
			elementRef.current = placeAutocomplete;

			placeAutocomplete.addEventListener("gmp-select", handleSelect);

			inputListener = (event) => {
				const nextValue = event?.target?.value ?? placeAutocomplete.value ?? "";
				onChangeRef.current?.(nextValue);
			};
			placeAutocomplete.addEventListener("input", inputListener);
		};

		init();

		return () => {
			isMounted = false;
			if (placeAutocomplete) {
				placeAutocomplete.removeEventListener("gmp-select", handleSelect);
				if (inputListener) {
					placeAutocomplete.removeEventListener("input", inputListener);
				}
				placeAutocomplete.remove();
			}
			elementRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [placeholder]);

	useEffect(() => {
		if (elementRef.current && value !== elementRef.current.value) {
			elementRef.current.value = value || "";
		}
	}, [value]);

	return (
		<div
			ref={containerRef}
			className="kyc-map-place-autocomplete-wrap"
			style={{ colorScheme: "light" }}
		/>
	);
};

export default KycMapPlaceInput;
